import React from 'react';
import InitialAvatar from '../InitialAvatar';
import Icon from './UaIcon';
import { ST_LABEL, deptList, fullName, joinDepts, roleById, stKey } from './uaModel';

/** Small pieces shared by the User Administration screens. */

/** Bridge's initials avatar. Only your own row shows your saved photo. */
export const Avatar = ({ person, size = 34, isMe = false, ghost = false }) => (
    <InitialAvatar
        name={fullName(person)} size={size} useStoredAvatar={isMe} className={`ua-av${ghost ? ' ua-av-ghost' : ''}`}
        style={{ flex: 'none', ...(ghost ? { background: 'var(--ua-hover)', color: 'var(--ua-muted)' } : null) }}
    />
);

export const StatusPill = ({ status }) => <span className={`ua-status ua-st-${status}`}>{ST_LABEL[status]}</span>;

export const PersonStatus = ({ person }) => <StatusPill status={stKey(person)} />;

/** On/off switch for one page. */
export const PageSwitch = ({ label, on, locked = false, onChange }) => (
    <label className="ua-switch">
        <input
            type="checkbox" role="switch" checked={!!on} disabled={locked} aria-label={label}
            onChange={(e) => onChange && onChange(e.target.checked ? 1 : 0)}
        />
        <span className="ua-track" />
        <span className="ua-sw-st">{on ? 'On' : 'Off'}</span>
    </label>
);

/** Role dropdown with the role's color dot in front. */
export const RoleSelect = ({ data, value, onChange, disabled = false, title, label, className = '' }) => {
    const role = roleById(data, value);
    return (
        <span className={`ua-rolesel ${className}`} style={{ '--ua-rc': role ? role.color : 'var(--ua-faint)' }}>
            <select className="ua-sel" value={value} disabled={disabled} title={title} aria-label={label} onChange={(e) => onChange(e.target.value)}>
                {data.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
        </span>
    );
};

export const Field = ({ id, label, error, help, full, children }) => (
    <div className={`ua-f${full ? ' ua-full' : ''}${error ? ' ua-err' : ''}`}>
        <label htmlFor={id}>{label}</label>
        {children}
        {error ? <span className="ua-emsg">{error}</span> : help ? <span className="ua-help">{help}</span> : null}
    </div>
);

/**
 * Pick one or more departments. `value` is the stored text ("Warehouse, Inspection"); `onChange` gets
 * the same form back. Departments the person already has that aren't in the list stay pickable.
 */
export const DeptPicker = ({ id, label, departments, value, onChange, error, help }) => {
    const chosen = deptList(value);
    const options = departments.concat(chosen.filter((d) => !departments.includes(d)));
    const toggle = (d, on) => onChange(joinDepts(on ? options.filter((x) => x === d || chosen.includes(x)) : chosen.filter((x) => x !== d)));
    return (
        <fieldset id={id} className={`ua-f ua-full ua-depts-f${error ? ' ua-err' : ''}`}>
            <legend>{label}{chosen.length ? <span className="ua-depts-n">{chosen.length} selected</span> : null}</legend>
            <div className="ua-depts">
                {options.map((d) => (
                    <label key={d} className={`ua-dept${chosen.includes(d) ? ' ua-dept-on' : ''}`}>
                        <input type="checkbox" checked={chosen.includes(d)} onChange={(e) => toggle(d, e.target.checked)} />
                        <span>{d}</span>
                    </label>
                ))}
            </div>
            {error ? <span className="ua-emsg">{error}</span> : help ? <span className="ua-help">{help}</span> : null}
        </fieldset>
    );
};

export const Note = ({ warn = false, children }) => (
    <div className={`ua-note${warn ? ' ua-note-warn' : ''}`}>
        <Icon name={warn ? 'alert' : 'info'} size={16} />
        <span>{children}</span>
    </div>
);

/** "Field: old → new" chips for a log entry, plus its note. */
export const Changes = ({ entry }) => (
    <>
        {entry.changes.length ? (
            <span className="ua-chg">
                {entry.changes.map((c, i) => (
                    <span key={i}>{c[0]}: {c[1] ? <><s>{c[1]}</s> → </> : null}<b>{c[2]}</b></span>
                ))}
            </span>
        ) : null}
        {entry.note ? <span className="ua-chg"><span>{entry.note}</span></span> : null}
    </>
);
